import React, { useState, useEffect } from 'react';
import './ProfileSettings.css';

const ProfileSettings = () => {
  const [userData, setUserData] = useState({
    email: '',
    city: '',
    zip: '',
    country_code: '',
    phone_number: '',
    lat: null,
    lng: null
  });
  const [originalUserData, setOriginalUserData] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          // User is not authenticated, redirect to login
          window.location.href = '/login';
          return;
        }

        const response = await fetch('/api/user/profile', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const data = await response.json();
        console.log('Fetched user profile data:', data);
        
        setUserData({
          email: data.email || '',
          phone_number: data.phone_number || '', // Ensure phone_number is set from the database
          city: data.city || '',
          zip: data.zip || '',
          country_code: data.country_code || '',
          lat: data.lat || null,
          lng: data.lng || null
        });
        setOriginalUserData(data);
        
        // Check email verification status if that data is available
        if (data.hasOwnProperty('is_email_verified')) {
          setIsEmailVerified(data.is_email_verified);
        }
      } catch (err) {
        setError('Failed to load profile data. Please try again later.');
        console.error('Error fetching user profile:', err);
      }
    };

    fetchUserProfile();
    // Also fetch captcha on component mount
    fetch('/api/captcha')
      .then(response => response.json())
      .then(data => {
        setCaptchaQuestion(data.question);
        setCaptchaToken(data.token);
      })
      .catch(error => {
        console.error('Error fetching captcha:', error);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    
    try {
      const addressChanged = 
        userData.city !== originalUserData?.city ||
        userData.zip !== originalUserData?.zip ||
        userData.country_code !== originalUserData?.country_code;

      let geoData = { lat: userData.lat, lng: userData.lng };

      if (addressChanged) {
        try {
          const query = `${userData.zip} ${userData.city} ${userData.country_code}`;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
          );

          if (!response.ok) throw new Error('Geolocation service failed');

          const data = await response.json();
          if (data && data.length > 0) {
            geoData = {
              lat: parseFloat(data[0].lat),
              lng: parseFloat(data[0].lon)
            };
          } else {
            throw new Error('Location not found');
          }
        } catch (err) {
          setError('Address could not be found');
          return;
        }
      }

      const token = localStorage.getItem('token');
      const response = await fetch('/api/user/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...userData,
          lat: geoData.lat,
          lng: geoData.lng,
          captchaAnswer,
          captchaToken,
          password: newPassword // include new password if provided
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Update failed');
      }

      setMessage('Profile updated successfully!');
      setOriginalUserData(data);

      // Fetch new captcha after update attempt
      const captchaResponse = await fetch('/api/captcha');
      const captchaData = await captchaResponse.json();
      setCaptchaQuestion(captchaData.question);
      setCaptchaToken(captchaData.token);
      setCaptchaAnswer('');
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'An error occurred while updating your profile');
      // Refresh captcha on error
      try {
        const captchaResponse = await fetch('/api/captcha');
        const captchaData = await captchaResponse.json();
        setCaptchaQuestion(captchaData.question);
        setCaptchaToken(captchaData.token);
      } catch (captchaError) {
        console.error('Error refreshing captcha:', captchaError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const response = await fetch('/api/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userData.email })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      setMessage(data.message);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="profile-settings">
      <h2>Profile Settings Update</h2>
      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
      
      {!isEmailVerified && (
        <div className="verification-warning">
          <p>Ihre E-Mail-Adresse ist nicht verifiziert. Sie können keine Angebote im Audio-Gebrauchtmarkt erstellen, bis Ihre E-Mail bestätigt ist.</p>
          <button onClick={handleResendVerification} className="resend-button">
            Bestätigungs-E-Mail erneut senden
          </button>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div>
          <label>
            E-Mail:
            <input
              type="email"
              value={userData.email}
              onChange={e => setUserData({...userData, email: e.target.value})}
              required
            />
          </label>
        </div>
        
        <div>
          <label>
            Telefonnummer (optional):
            <input
              type="tel"
              value={userData.phone_number || ''}
              onChange={e => setUserData({...userData, phone_number: e.target.value})}
              placeholder="+49 123 4567890"
            />
            <small className="phone-hint">
              Die Telefonnummer ist optional. Sie können bei jedem Angebot wählen, ob diese angezeigt werden soll.
            </small>
          </label>
        </div>
        
        <div>
          <label>
            City:
            <input
              type="text"
              value={userData.city}
              onChange={e => setUserData({...userData, city: e.target.value})}
            />
          </label>
        </div>
        
        <div>
          <label>
            ZIP:
            <input
              type="text"
              value={userData.zip}
              onChange={e => setUserData({...userData, zip: e.target.value})}
            />
          </label>
        </div>
        
        <div>
          <label>
            Country Code (2 letters):
            <input
              type="text"
              value={userData.country_code}
              onChange={e => setUserData({...userData, country_code: e.target.value.toUpperCase()})}
              maxLength="2"
              pattern="[A-Za-z]{2}"
            />
          </label>
        </div>
        
        <div className="form-group">
          <label>New Password (optional)</label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="Neues Passwort"
          />
        </div>

        {captchaQuestion && (
          <div className="form-group">
            <label>{captchaQuestion}</label>
            <input
              type="text"
              value={captchaAnswer}
              onChange={e => setCaptchaAnswer(e.target.value)}
              required
              placeholder="Enter answer here"
            />
          </div>
        )}
        
        <button type="submit" disabled={submitting}>Update Profile</button>
      </form>
    </div>
  );
};

export default ProfileSettings;